import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { inputCls } from "@/components/kit";
import { ShareButton, eventPhrase } from "@/components/ShareDialog";
import { api, errMsg, fmtMoney, fmtValue } from "@/lib/api";

const highSeason = (d) => d && [5, 6, 7, 8, 9].includes(Number(d.slice(5, 7)));

export function CatalogPicker({ q, change }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/catalog").then((r) => setItems(r.data.filter((x) => x.attivo !== false))).catch(() => {});
  }, []);
  const list = items.filter((x) => !(x.eventi || []).length || x.eventi.includes(q.evento_tipo));
  if (!list.length) return <div className="text-xs text-[#7B929C]">Suggerimento: inserisci menù, pacchetti e servizi nel «Listino» per aggiungerli qui con un tocco.</div>;
  const price = (x) => (highSeason(q.data.evento_data) && x.prezzo_alta !== "" && x.prezzo_alta != null ? Number(x.prezzo_alta) : Number(x.prezzo || 0));
  const apply = (x) => {
    if (x.unita === "a persona" && x.categoria !== "Servizio") {
      const note = [q.data.note, `${x.nome}: ${x.descrizione || ""}`].filter(Boolean).join("\n");
      change({ data: { ...q.data, prezzo_adulti: String(price(x)), note } });
    } else {
      change({ items: [...(q.items || []), { servizio: x.nome, quantita: x.unita === "a persona" ? Number(q.data.adulti || 0) + Number(q.data.bambini || 0) || 1 : 1, unitario: price(x) }] });
    }
    toast.success(`${x.nome} inserito`);
  };
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="catalog-picker">
      <span className="text-xs text-[#7B929C]">Dal listino{highSeason(q.data.evento_data) ? " (alta stagione)" : ""}:</span>
      {list.map((x) => <button key={x.id} onClick={() => apply(x)} data-testid={`catalog-pick-${x.id}`} className="text-xs px-3 py-1.5 rounded-full border border-[#0B6E8F]/40 bg-[#EAF5F7] hover:bg-[#DDEFF4]">{x.nome} • {fmtMoney(price(x))}</button>)}
    </div>
  );
}

export function AiDraft({ q, change }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ai/quote-draft", { testo: text, evento_tipo: q.evento_tipo || null });
      const d = r.data;
      const note = [d.presentazione, q.data.note].filter(Boolean).join("\n\n");
      change({ evento_tipo: d.evento_tipo, data: { ...q.data, ...d.data, note }, items: [...(q.items || []), ...d.items] });
      toast.success("Bozza preparata: controlla e modifica prima di inviare");
      setText("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-[#34B3B0]/50 bg-gradient-to-br from-[#EAF5F7] to-white p-5 space-y-3" data-testid="ai-draft">
      <div className="flex items-center gap-2 font-serif-it text-xl font-semibold"><Sparkles size={18} className="text-[#34B3B0]" /> Assistente proposte</div>
      <p className="text-xs text-[#47606B]">Scrivi la richiesta con parole tue: l'assistente propone menù, servizi dal listino e un testo di presentazione elegante.</p>
      <textarea rows={2} className={inputCls} value={text} onChange={(e) => setText(e.target.value)} placeholder="es. matrimonio 120 persone a settembre alla Tenuta Ledo, menù di pesce, budget 15.000 €, vogliono open bar e fiori bianchi" data-testid="ai-draft-input" />
      <Button className="rounded-full" onClick={run} disabled={busy || text.trim().length < 5} data-testid="ai-draft-button">{busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Sto preparando…</> : <><Sparkles size={14} className="mr-2" /> Prepara la bozza</>}</Button>
    </div>
  );
}

export function OnlineQuote({ q, id, reload }) {
  const [url, setUrl] = useState("");
  const make = async () => {
    try {
      const r = await api.post(`/quotes/${id}/online`);
      setUrl(window.location.origin + r.data.path);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!["bozza", "inviato"].includes(q.status)) return null;
  return (
    <div className="space-y-2" data-testid="online-quote">
      <div className="text-sm text-[#47606B] inline-flex items-center gap-2"><Globe size={14} /> Il cliente può leggerlo e accettarlo online: il contratto si prepara da solo.</div>
      {!url ? <Button variant="outline" size="sm" className="rounded-full" onClick={make} data-testid="online-quote-create">Crea link per accettare online</Button> : (
        <div className="space-y-2">
          <div className="text-xs break-all text-[#7B929C]" data-testid="online-quote-url">{url}</div>
          <ShareButton size="sm" testId="online-quote-whatsapp" label="Invia link su WhatsApp" link={url} linkLabel="👉 Consulti e confermi qui:" phone={q.data.cliente_tel}
            body={`🌟 Gentile *${q.data.cliente_nome || "Cliente"}*,\n\n🙏 grazie per averci scelto! Ecco la nostra proposta per ${eventPhrase(q.evento_tipo)}${q.data.evento_data ? ` del 📅 *${fmtValue({ type: "date" }, q.data.evento_data)}*` : ""}. 🍽️✨\n✅ Può consultarla e confermarla con un tocco dal link qui sotto.\n\n💬 Siamo a disposizione per ogni personalizzazione.\n\nUn cordiale saluto 🌊`} />
        </div>
      )}
    </div>
  );
}
